import { useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", email: "", phone: "", venue: "", city: "", event_date: "", message: "", website: "" };

const inputCls =
  "h-12 w-full border border-neutral-800 bg-black/40 px-4 text-base text-neutral-100 placeholder:text-neutral-600 focus:border-red-700 focus:outline-none disabled:opacity-60";
const labelCls = "font-mono-tac block text-[10px] uppercase tracking-[0.3em] text-neutral-500";

function Field({ id, label, children }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function BookingForm() {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const busy = state === "loading";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2 || form.venue.trim().length < 2 || form.city.trim().length < 2) {
      setError("Compila nome, locale/evento e città.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }
    setState("loading");
    try {
      const res = await fetch(`${API}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setState("done");
      toast("Richiesta inviata. Vi rispondiamo presto.", { description: form.venue });
    } catch {
      setState("idle");
      setError("Qualcosa è andato storto. Riprova o scrivici via email.");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="booking-success" className="border border-red-900/50 bg-[#0D0D0D] p-8">
        <span className="inline-flex h-10 w-10 items-center justify-center bg-[#C8102E] text-white">
          <Check className="h-5 w-5" />
        </span>
        <h3 className="font-display mt-5 text-3xl uppercase text-neutral-100">Richiesta ricevuta.</h3>
        <p className="mt-2 text-sm text-neutral-400">
          Ci arriva subito via email. Ti rispondiamo a {form.email} il prima possibile.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="booking-form" onSubmit={submit} noValidate className="border border-neutral-800 bg-[#0D0D0D]/90 p-6 backdrop-blur sm:p-8">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field id="bk-name" label="Il tuo nome *">
          <input id="bk-name" data-testid="booking-name-input" value={form.name} onChange={set("name")} disabled={busy} className={inputCls} placeholder="Nome e cognome" />
        </Field>
        <Field id="bk-email" label="Email *">
          <input id="bk-email" data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} disabled={busy} className={inputCls} placeholder="nome@esempio.it" />
        </Field>
        <Field id="bk-phone" label="Telefono">
          <input id="bk-phone" data-testid="booking-phone-input" value={form.phone} onChange={set("phone")} disabled={busy} className={inputCls} placeholder="+39 …" />
        </Field>
        <Field id="bk-date" label="Data indicativa">
          <input id="bk-date" data-testid="booking-date-input" value={form.event_date} onChange={set("event_date")} disabled={busy} className={inputCls} placeholder="es. 20 dicembre 2026" />
        </Field>
        <Field id="bk-venue" label="Locale / evento *">
          <input id="bk-venue" data-testid="booking-venue-input" value={form.venue} onChange={set("venue")} disabled={busy} className={inputCls} placeholder="Nome del locale o del festival" />
        </Field>
        <Field id="bk-city" label="Città *">
          <input id="bk-city" data-testid="booking-city-input" value={form.city} onChange={set("city")} disabled={busy} className={inputCls} placeholder="Città" />
        </Field>
      </div>
      <div className="mt-5">
        <label htmlFor="bk-message" className={labelCls}>Messaggio</label>
        <textarea id="bk-message" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")} disabled={busy} className={`${inputCls} h-auto py-3`} placeholder="Raccontaci l'evento: pubblico, orari, impianto…" />
      </div>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p data-testid="booking-error" role="alert" className={`text-xs ${error ? "text-red-500" : "text-neutral-600"}`}>
          {error || "* campi obbligatori. Rispondiamo a tutti."}
        </p>
        <button type="submit" data-testid="booking-submit-button" disabled={busy} className="inline-flex h-12 items-center gap-3 bg-[#C8102E] px-7 font-mono-tac text-sm uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-[#FF2A4B] hover:shadow-[0_0_30px_rgba(200,16,46,0.5)] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Invia richiesta<ArrowRight className="h-4 w-4" /></>)}
        </button>
      </div>
    </form>
  );
}
